/**
 * CSV export dialog. Three scopes:
 *
 *   Page Rows      — exactly what's currently rendered (respects
 *                    pagination, sort, and filter — uses the rows
 *                    already in memory; no extra fetch).
 *   All Rows       — everything matching the current filter + sort,
 *                    paginated server-side until we've got them all.
 *   All Data       — entire table, ignoring filter/sort/pagination.
 *
 * The user picks a filename (defaulting to `<schema>.<table>_<date>.csv`)
 * and a scope, then clicks Export. Where the browser supports it, we
 * raise a real "Save As" dialog via `window.showSaveFilePicker`; on
 * Safari / Firefox we fall back to a download to the default folder.
 */
import { useRef, useState } from "react";
import {
  Button,
  Group,
  Progress,
  Radio,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ColumnMeta, RowsPage } from "../api/types";
import { defaultExportFilename, rowsToCsv, saveCsv } from "../lib/csv";
import { showPendingNotification } from "../lib/pending-ops";

const PAGE_SIZE = 500;

type Scope = "page" | "filtered" | "all";

export function ExportModal({
  schema,
  name,
  columns,
  pageRows,
  totalAfterFilter,
  currentFilter,
  currentSort,
  fetchPage,
  onClose,
}: {
  schema: string;
  name: string;
  columns: ColumnMeta[];
  pageRows: Record<string, unknown>[];
  totalAfterFilter: number;
  currentFilter?: string;
  currentSort?: string;
  fetchPage: (
    page: number,
    pageSize: number,
    filter?: string,
    sort?: string,
    signal?: AbortSignal,
  ) => Promise<RowsPage>;
  onClose: () => void;
}) {
  const [filename, setFilename] = useState(() =>
    defaultExportFilename(schema, name),
  );
  const [scope, setScope] = useState<Scope>("page");
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  async function collectRows(
    s: Scope,
    signal: AbortSignal,
  ): Promise<Record<string, unknown>[]> {
    if (s === "page") {
      return pageRows;
    }
    const filter = s === "filtered" ? currentFilter : undefined;
    const sort = s === "filtered" ? currentSort : undefined;

    const out: Record<string, unknown>[] = [];
    let page = 0;
    let total = totalAfterFilter; // fallback initial estimate
    // Loop until the server says we've collected the whole result set
    // (or returns an empty page, defending against off-by-one).
    while (true) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const resp = await fetchPage(page, PAGE_SIZE, filter, sort, signal);
      out.push(...resp.rows);
      total = resp.total ?? total;
      const pct = total > 0 ? Math.round((out.length / total) * 100) : 100;
      setProgress(pct);
      setProgressLabel(`${out.length.toLocaleString()} of ${total.toLocaleString()} rows`);
      if (out.length >= total) break;
      if (resp.rows.length === 0) break;
      page++;
    }
    return out;
  }

  async function doExport() {
    setExporting(true);
    setProgress(0);
    setProgressLabel("");

    // One pending-op notification covers the whole multi-page loop.
    // The shared controller is what both the bottom-right toast's
    // Cancel button and our own modal's "Stop export" button abort.
    const controller = new AbortController();
    abortRef.current = controller;
    const handle = showPendingNotification(
      `Exporting ${schema}.${name}…`,
      controller,
    );

    try {
      const rows = await collectRows(scope, controller.signal);
      const csv = rowsToCsv(columns, rows);
      const saved = await saveCsv(filename, csv);
      if (saved) {
        notifications.show({
          message: `Exported ${rows.length.toLocaleString()} rows to ${filename}`,
          color: "teal",
        });
        onClose();
      }
    } catch (err) {
      const e = err as Error;
      if (e.name === "AbortError") {
        notifications.show({ message: "Export cancelled", color: "gray" });
      } else {
        notifications.show({
          message: `Export failed: ${e.message}`,
          color: "red",
        });
      }
    } finally {
      handle.hide();
      abortRef.current = null;
      setExporting(false);
    }
  }

  const supportsSaveAs =
    typeof (window as Window & { showSaveFilePicker?: unknown })
      .showSaveFilePicker === "function";

  return (
    <Stack gap="md">
      <TextInput
        label="Filename"
        value={filename}
        onChange={(e) => setFilename(e.currentTarget.value)}
        disabled={exporting}
        autoFocus
      />
      {!supportsSaveAs && (
        <Text size="xs" c="dimmed">
          The file will save to your browser's default download folder.
          To pick a destination, use Chrome or Edge.
        </Text>
      )}

      <Radio.Group
        label="Rows to export"
        value={scope}
        onChange={(v) => setScope(v as Scope)}
      >
        <Stack gap="xs" mt="xs">
          <Radio
            value="page"
            disabled={exporting}
            label="Current page"
            description={`${pageRows.length.toLocaleString()} rows shown — respects pagination, sort and filter`}
          />
          <Radio
            value="filtered"
            disabled={exporting}
            label="All rows matching current filter & sort"
            description={`Up to ${totalAfterFilter.toLocaleString()} rows — paginates through the API`}
          />
          <Radio
            value="all"
            disabled={exporting}
            label="All data"
            description="Entire table, ignoring current filter and sort"
          />
        </Stack>
      </Radio.Group>

      {exporting && (
        <Stack gap={4}>
          <Progress value={progress} animated size="sm" />
          <Text size="xs" c="dimmed" ta="center">
            {progressLabel || "Preparing…"}
          </Text>
        </Stack>
      )}

      <Group justify="flex-end" mt="sm">
        {exporting ? (
          <Button
            variant="default"
            color="red"
            onClick={() => abortRef.current?.abort()}
          >
            Stop export
          </Button>
        ) : (
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button onClick={doExport} loading={exporting} disabled={exporting}>
          Export
        </Button>
      </Group>
    </Stack>
  );
}
